import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { ApiError, NetworkError, ValidationError, AuthenticationError } from '@/lib/api-errors';
import { sanitizeEmail } from '@/lib/sanitize';
import { RateLimiters } from '@/lib/rate-limiter';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";
import { logger } from "@/utils/logger";
import { supabase } from "@/integrations/supabase/client";

export default function Login() {
  logger.component('Login', 'Component rendering started');
  const [formData, setFormData] = useState({
    email: "",
    password: ""
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const {
    signIn,
    user,
    loading: authLoading
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleInputChange = (field: string, value: string | boolean) => {
    if (field === 'email' && typeof value === 'string') {
      value = sanitizeEmail(value);
    }
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const rateCheck = RateLimiters.auth.check();
    if (!rateCheck.allowed) {
      toast({
        title: "Too many attempts",
        description: `Please wait ${Math.ceil((rateCheck.retryAfter || 0) / 1000)} seconds before trying again.`,
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    RateLimiters.auth.record();

    try {
      const { error } = await signIn(formData.email, formData.password);
      if (error) {
        if (error.message.includes('Email not confirmed')) {
          toast({
            title: "Email not verified",
            description: "Please check your email and verify your account before signing in.",
            variant: "destructive"
          });
          navigate(`/register/verify`, { state: { email: formData.email } });
        } else {
          toast({
            title: "Sign in failed",
            description: error.message,
            variant: "destructive"
          });
        }
      } else {
        const { data: { session: freshSession } } = await supabase.auth.getSession();
        const currentUser = freshSession?.user;

        if (currentUser && !currentUser.email_confirmed_at) {
          toast({
            title: "Email not verified",
            description: "Please verify your email before accessing the dashboard.",
            variant: "destructive"
          });
          navigate(`/register/verify`, { state: { email: formData.email } });
          return;
        }

        if (currentUser) {
          const { data: profile } = await supabase.
          from('profiles').
          select('role').
          eq('user_id', currentUser.id).
          single();

          if (!profile?.role) {
            toast({
              title: "Welcome!",
              description: "Let's complete your profile setup."
            });
            navigate('/auth/complete-profile', { replace: true });
            return;
          }
        }

        toast({
          title: "Welcome back!",
          description: "You have successfully signed in."
        });

        const from = location.state?.from?.pathname || '/dashboard';
        navigate(from, { replace: true });
      }
    } catch (error) {
      if (error instanceof AuthenticationError) {
        toast({
          title: "Login Failed",
          description: "Invalid email or password. Please try again.",
          variant: "destructive"
        });
        Sentry.captureException(error, {
          tags: { context: 'auth_error', operation: 'login', component: 'Login' },
          extra: { message: error.message }
        });
      } else if (error instanceof NetworkError) {
        toast({
          title: "Connection Error",
          description: "Unable to connect. Check your internet connection and try again.",
          variant: "destructive"
        });
        Sentry.captureException(error, {
          tags: { context: 'network_error', operation: 'login' },
          extra: { message: error.message }
        });
      } else if (error instanceof ValidationError) {
        toast({
          title: "Validation Error",
          description: "Please enter valid email and password.",
          variant: "destructive"
        });
        Sentry.captureException(error, {
          tags: { context: 'validation_error', operation: 'login' },
          extra: { message: error.message }
        });
      } else {
        toast({
          title: "Login Failed",
          description: "Something went wrong. Please try again.",
          variant: "destructive"
        });
        Sentry.captureException(error, {
          tags: { context: 'unknown_error', operation: 'login' },
          extra: { message: 'Unknown login error' }
        });
      }
    } finally {
      setLoading(false);
    }
  };

  if (!signIn) {
    Sentry.captureException(new Error('[Login] signIn function not available from auth context'), {
      tags: { context: 'auth_error', operation: 'login', component: 'Login' },
      extra: { message: 'signIn function not available from auth context' }
    });
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-sm w-full text-center space-y-4">
          <h2 className="text-xl font-semibold text-destructive">Authentication Error</h2>
          <p className="text-sm text-muted-foreground">
            Unable to load authentication system. Please try refreshing the page.
          </p>
          <Button onClick={() => window.location.reload()} className="w-full">
            Refresh Page
          </Button>
        </div>
      </div>);

  }

  logger.component('Login', 'Rendering main login form');

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-[400px] space-y-8">
          {/* Logo Mark */}
          <div className="flex justify-center">
            














          
          </div>

          {/* Heading */}
          <h1 className="text-2xl font-bold text-foreground text-center">Welcome back</h1>

          {/* Google OAuth placeholder button */}
          <button
          type="button"
          className="w-full flex items-center justify-center gap-3 rounded-md border border-border bg-background px-4 py-2.5 text-sm font-medium text-foreground hover:bg-muted transition-colors"
          onClick={() => {}}>
          
            <svg width="20" height="20" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
            </svg>
            Continue with Google
          </button>

          {/* Divider */}
          <div className="flex items-center gap-4">
            <div className="h-px flex-1 bg-border" />
            <span className="text-sm text-muted-foreground">or</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Input
              id="email"
              type="email"
              placeholder="Enter your email"
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              required />
            
            </div>

            <div>
              <div className="relative">
                <Input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                value={formData.password}
                onChange={(e) => handleInputChange("password", e.target.value)}
                className="pr-10"
                required />
              
                <button
                type="button"
                className="absolute inset-y-0 right-0 flex items-center pr-3"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}>
                
                  {showPassword ?
                <EyeOff className="h-4 w-4 text-muted-foreground" /> :

                <Eye className="h-4 w-4 text-muted-foreground" />
                }
                </button>
              </div>
              <div className="flex justify-end mt-1.5">
                <Link
                to="/auth/forgot-password"
                className="text-xs text-muted-foreground hover:text-primary transition-colors">
                
                  Forgot password?
                </Link>
              </div>
            </div>

            <Button
            type="submit"
            className="w-full"
            disabled={loading}>
            
              {loading ? "Signing in…" : "Sign In"}
            </Button>
          </form>

          {/* Legal links */}
          <p className="text-xs text-muted-foreground text-center">
            By continuing, you agree to our{" "}
            <a href="#" className="underline hover:text-foreground transition-colors">Terms</a>{" "}
            and{" "}
            <a href="#" className="underline hover:text-foreground transition-colors">Privacy Policy</a>.
          </p>

          {/* Sign up prompt */}
          <p className="text-sm text-muted-foreground text-center">
            Don't have an account?{" "}
            <Link to="/register" className="text-primary hover:underline font-medium">
              Sign up
            </Link>
          </p>
        </div>
    </div>);

}